import { default as React } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { InfoCard } from '@backstage/core-components';
import { Box, Typography } from '@material-ui/core';
import { makeStyles } from '@mui/styles';

const useStyles = makeStyles(theme => ({
  cardContent: {
    padding: theme.spacing(2),
  },
  section: {
    marginBottom: theme.spacing(3),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  sectionTitle: {
    fontSize: '0.875rem',
    fontWeight: 700,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1.5),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  infoRow: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: theme.spacing(2),
    width: '100%',
  },
  label: {
    color: 'rgba(0, 0, 0, 0.54)',
    fontWeight: 600,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: theme.spacing(0.5),
    display: 'block',
  },
  value: {
    color: theme.palette.text.primary,
    fontSize: '0.875rem',
    wordBreak: 'break-word',
    display: 'block',
    lineHeight: 1.4,
  },
  horizontalSection: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: theme.spacing(1),
    width: '100%',
  },
  horizontalGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: theme.spacing(4),
    marginTop: theme.spacing(1),
    width: '100%',
  },
  description: {
    color: theme.palette.text.primary,
    fontSize: '0.875rem',
    lineHeight: 1.5,
    marginBottom: theme.spacing(1.5),
  },
  chip: {
    height: '24px',
    fontSize: '0.75rem',
    marginRight: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
  },
  chipContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
  },
  divider: {
    margin: theme.spacing(2, 0),
  },
  link: {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  relationCard: {
    padding: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.default,
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  relationLabel: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: theme.palette.text.secondary,
  },
  relationValue: {
    fontSize: '0.875rem',
    color: theme.palette.text.primary,
    fontWeight: 500,
  },
}));

export const JsmEntityDetailsCard = ({ variant }: { variant?: 'gridItem' }) => {
  const { entity } = useEntity();
  const classes = useStyles();

  // Extract entity metadata
  const { metadata } = entity;
  const annotations = metadata?.annotations;

  // Get JSM Engineering info from annotations
  const jsmEngineeringTeam = annotations?.['jsm/engineering-team'] || '';
  const jsmEngineeringEmailCodeRed =
    annotations?.['jsm/engineering-team-email-code-red'] || '';
  const jsmEngineeringChannelCodeOrange =
    annotations?.['jsm/teams-channel-code-orange'] || '';

  // Get JSM Support info from annotations
  const jsmSupportTeam = annotations?.['jsm/support-team'] || '';
  const jsmSupportEmail = annotations?.['jsm/support-team-email'] || '';

  if (
    !jsmEngineeringTeam &&
    !jsmEngineeringEmailCodeRed &&
    !jsmEngineeringChannelCodeOrange &&
    !jsmSupportTeam &&
    !jsmSupportEmail
  ) {
    return null; // Don't render the card if no JSM info is available
  }

  return (
    <InfoCard title="JSM information" variant={variant}>
      <Box className={classes.cardContent}>
        {/* JSM Engineering and Support Sections - Horizontal Layout */}
        <Box className={classes.horizontalGrid}>
          {/* JSM Engineering Section */}
          {(jsmEngineeringTeam ||
            jsmEngineeringEmailCodeRed ||
            jsmEngineeringChannelCodeOrange) && (
            <Box className={classes.horizontalSection}>
              <Typography className={classes.sectionTitle}>
                JSM ENGINEERING
              </Typography>
              {jsmEngineeringTeam && jsmEngineeringTeam !== 'n/a' && (
                <Box className={classes.infoRow}>
                  <Typography className={classes.label}>JSM Team:</Typography>
                  <Typography className={classes.value}>
                    {jsmEngineeringTeam}
                  </Typography>
                </Box>
              )}
              {jsmEngineeringEmailCodeRed &&
                jsmEngineeringEmailCodeRed !== 'n/a' && (
                  <Box className={classes.infoRow}>
                    <Typography className={classes.label}>
                      JSM Email:
                    </Typography>
                    <Typography className={classes.value}>
                      <a
                        href={`mailto:${jsmEngineeringEmailCodeRed}`}
                        className={classes.link}
                      >
                        {jsmEngineeringEmailCodeRed}
                      </a>
                    </Typography>
                  </Box>
                )}
              {jsmEngineeringChannelCodeOrange &&
                jsmEngineeringChannelCodeOrange !== 'n/a' && (
                  <Box className={classes.infoRow}>
                    <Typography className={classes.label}>
                      Teams Channel:
                    </Typography>
                    <Typography className={classes.value}>
                      <a
                        href={`https://teams.microsoft.com/l/channel/${
                          jsmEngineeringChannelCodeOrange.split('@')[0]
                        }/General?groupId=${
                          jsmEngineeringChannelCodeOrange.split('.')[0]
                        }&tenantId=${jsmEngineeringChannelCodeOrange
                          .split('@')[1]
                          ?.replace('.teams.ms', '')}`}
                        className={classes.link}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {jsmEngineeringChannelCodeOrange}
                      </a>
                    </Typography>
                  </Box>
                )}
            </Box>
          )}

          {/* JSM Support Section */}
          {(jsmSupportTeam || jsmSupportEmail) && (
            <Box className={classes.horizontalSection}>
              <Typography className={classes.sectionTitle}>
                JSM SUPPORT
              </Typography>
              {jsmSupportTeam && jsmSupportTeam !== 'n/a' && (
                <Box className={classes.infoRow}>
                  <Typography className={classes.label}>JSM Team:</Typography>
                  <Typography className={classes.value}>
                    {jsmSupportTeam}
                  </Typography>
                </Box>
              )}
              {jsmSupportEmail && jsmSupportEmail !== 'n/a' && (
                <Box className={classes.infoRow}>
                  <Typography className={classes.label}>JSM Email:</Typography>
                  <Typography className={classes.value}>
                    <a
                      href={`mailto:${jsmSupportEmail}`}
                      className={classes.link}
                    >
                      {jsmSupportEmail}
                    </a>
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </InfoCard>
  );
};
